import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, Minimize2 } from 'lucide-react';
import { ClipLoader } from 'react-spinners';

import styles from './styles/VideoPlayer.module.css';

const VideoPlayer = ({
    src,
    type = 'video/mp4',
    autoPlay = true,
    muted = true,
    loop = true,
    showSoundButton = true,
    showControls = true,
    onCanPlay,
}) => {
    const [playing, setPlaying] = useState(autoPlay);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(muted || !showSoundButton);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [controlsTimeout, setControlsTimeout] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const isIOS = useMemo(
        () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
        []
    );

    const videoRef = useRef(null);
    const containerRef = useRef(null);

    // Add fullscreen change event listeners for different browsers
    const fullscreenChangeHandler = useCallback(() => {
        setIsFullscreen(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );
    }, []);

    useEffect(() => {
        // Initialize video properties
        if (videoRef.current) {
            // If sound button is hidden, ensure video is muted
            videoRef.current.muted = !showSoundButton || muted;

            if (autoPlay) {
                // Try to play and handle any autoplay restrictions
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => {
                        // Autoplay was prevented, update state
                        setPlaying(false);
                    });
                }
            }
        }

        document.addEventListener('fullscreenchange', fullscreenChangeHandler);
        document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
        document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
        document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);

        return () => {
            // Clean up event listeners
            document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
            document.removeEventListener('webkitfullscreenchange', fullscreenChangeHandler);
            document.removeEventListener('mozfullscreenchange', fullscreenChangeHandler);
            document.removeEventListener('MSFullscreenChange', fullscreenChangeHandler);

            // Clear any pending timeouts
            if (controlsTimeout) {
                clearTimeout(controlsTimeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPlay, muted, showSoundButton, fullscreenChangeHandler]);

    // Show/hide controls with timeout
    const handleControlsVisibility = useCallback(show => {
        if (controlsTimeout) {
            clearTimeout(controlsTimeout);
        }

        if (show) {
            setControlsVisible(true);
        } else {
            const timeout = setTimeout(() => {
                setControlsVisible(false);
            }, 2000);
            setControlsTimeout(timeout);
        }
    }, [controlsTimeout]);

    // Time update handler
    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
            setDuration(videoRef.current.duration);
        }
    }, []);

    // Format time to MM:SS
    const formatTime = useCallback(time => {
        if (isNaN(time)) return '00:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, []);

    // Play/pause toggle
    const togglePlay = useCallback(e => {
        e.stopPropagation(); // Prevent container click from triggering

        if (videoRef.current) {
            if (playing) {
                videoRef.current.pause();
            } else {
                // For iOS, we need to handle play in a user-initiated context
                videoRef.current.play().catch((error) => {
                    console.log('Video play was prevented:', error);
                });
            }
            setPlaying(!playing);
        }
    }, [playing]);

    // Toggle container click to play/pause
    const handleContainerClick = useCallback(() => {
        // Always toggle play on container click
        togglePlay({ stopPropagation: () => { } });

        // Also ensure controls are visible after a click, clearing any hide timeout
        handleControlsVisibility(true);
    }, [togglePlay, handleControlsVisibility]);

    // Mute toggle
    const toggleMute = useCallback(e => {
        e.stopPropagation(); // Prevent container click from triggering

        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    }, [isMuted]);

    // Toggle fullscreen with iOS-specific handling
    const toggleFullscreen = useCallback(e => {
        e.stopPropagation(); // Prevent container click from triggering

        if (!containerRef.current) return;

        if (!isFullscreen) {
            if (isIOS && videoRef.current.webkitEnterFullscreen) {
                // iOS devices use the video element's native fullscreen
                videoRef.current.webkitEnterFullscreen();
            } else if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            } else if (containerRef.current.webkitRequestFullscreen) {
                containerRef.current.webkitRequestFullscreen();
            } else if (containerRef.current.mozRequestFullScreen) {
                containerRef.current.mozRequestFullScreen();
            } else if (containerRef.current.msRequestFullscreen) {
                containerRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    }, [isFullscreen, isIOS]);

    // Handle video ready
    const handleCanPlay = useCallback(() => {
        setIsLoading(false);
    }, []);

    return (
        <div
            className={styles.videoPlayerContainer}
            ref={containerRef}
            onClick={handleContainerClick}
            onMouseEnter={() => handleControlsVisibility(true)}
            onMouseLeave={() => handleControlsVisibility(false)}
            onTouchStart={() => handleControlsVisibility(true)}
        >
            {isLoading && (
                <div className={styles.loaderContainer}>
                    <ClipLoader color="#ffffff" size={50} />
                </div>
            )}

            <video
                ref={videoRef}
                className={styles.videoElement}
                loop={loop}
                playsInline // Important for iOS
                onTimeUpdate={handleTimeUpdate}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onLoadedMetadata={handleTimeUpdate}
                onCanPlay={() => {
                    handleCanPlay();
                    if (onCanPlay) onCanPlay();
                }}
                onWaiting={() => setIsLoading(true)}
            >
                <source src={src} type={type} />
                Your browser does not support the video tag.
            </video>

            {/* Show controls based on showControls parameter */}
            {showControls ? (
                <div className={`${styles.controls} ${controlsVisible ? styles.visible : ''}`}>
                    <div className={styles.sideControls}>
                        <button
                            className={styles.button}
                            onClick={togglePlay}
                            aria-label={playing ? 'Pause' : 'Play'}
                        >
                            {playing ? <Pause /> : <Play />}
                        </button>
                        <div className={styles.timeDisplay}>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </div>
                    </div>

                    <div className={styles.sideControls}>
                        {/* Only show sound button if showSoundButton is true */}
                        {showSoundButton && (
                            <button
                                className={styles.button}
                                onClick={toggleMute}
                                aria-label={isMuted ? 'Unmute' : 'Mute'}
                            >
                                {isMuted ? <VolumeX /> : <Volume2 />}
                            </button>
                        )}
                        <button
                            className={styles.button}
                            onClick={toggleFullscreen}
                            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                        </button>
                    </div>
                </div>
            ) : (
                // If showControls is false, only show a simplified play/pause button in the middle
                <div className={`${styles.controls} ${controlsVisible ? styles.visible : ''}`}>
                    <button
                        className={styles.button}
                        onClick={togglePlay}
                        aria-label={playing ? 'Pause' : 'Play'}
                    >
                        {playing ? <Pause /> : <Play />}
                    </button>
                </div>
            )}
        </div>
    );
};

export default memo(VideoPlayer);
